import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { v4API } from '../../Api';
import { useV4 } from './V4Layout';
import { actionKey, errorMessage, formatMoney } from './common';
import { loadConfigurationReferences } from './Configuration';

const storageKey = 'v4-crypto-address-import';
function pendingImport() {
  try { const raw = sessionStorage.getItem(storageKey); if (!raw) return null;
    const value = JSON.parse(raw); if (!value.key || !Array.isArray(value.body?.addresses) || value.body.confirm !== 'v4-exclusive') throw new Error();
    return value;
  } catch { return { unreadable: true }; }
}
export default function CryptoAddresses() {
  const { system } = useV4();
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [processings, setProcessings] = useState([]);
  const [processingId, setProcessingId] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [addresses, setAddresses] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState(pendingImport);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const [history, setHistory] = useState(null);
  const historyRequest = useRef(0);
  const lock = useRef(false);
  useEffect(() => {
    let active = true;
    if (!system?.ready) return undefined;
    setLoading(true);
    Promise.all([v4API.cryptoAddresses({ page, ...(filter && { processingId: filter }) }), loadConfigurationReferences('processings')])
      .then(([rows, refs]) => { if (active) {
        setData(rows); setProcessings(refs.items.filter(row => ['btc', 'ltc'].includes(row.provider)));
        if (refs.truncated) setNotice('Справочник процессингов ограничен. Для отсутствующего подключения используйте API импорта.');
      } }).catch(err => { if (active) setError(errorMessage(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [system?.ready, filter, page, reload]);
  const save = async () => {
    if (lock.current || !system?.ready || pending?.unreadable) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const next = pending || { key: actionKey(), body: { processingId,
        addresses: addresses.split(/\r?\n/).map(value => value.trim()).filter(Boolean), reason: reason.trim(), confirm: 'v4-exclusive' } };
      sessionStorage.setItem(storageKey, JSON.stringify(next)); setPending(next);
      const result = await v4API.importCryptoAddresses(next.body, next.key);
      sessionStorage.removeItem(storageKey); setPending(null); setAddresses(''); setConfirmed(false);
      setNotice(`Добавлено адресов: ${result.imported}${result.replayed ? ' (повтор без дубликатов)' : ''}.`);
      setReload(value => value + 1);
    } catch (err) {
      if (!pending && [400, 404, 409, 422].includes(err?.response?.status)) { sessionStorage.removeItem(storageKey); setPending(null); }
      setError(errorMessage(err));
    } finally { lock.current = false; setBusy(false); }
  };
  const retire = async row => {
    if (lock.current || !system?.ready) return;
    if (!window.confirm(`Выключить адрес ${row.address} для новых заказов? Уже выданные платежи продолжат проверяться.`)) return;
    lock.current = true; setBusy(true);
    try { await v4API.retireCryptoAddress(row._id, { expectedRevision: row.revision, reason: 'Вывод адреса из пула через панель' });
      setReload(value => value + 1);
    } catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setBusy(false); }
  };
  const reuse = async row => {
    if (lock.current || !system?.ready) return;
    if (!window.confirm(`Включить многоразовый приём на ${row.address}? Платежи будут различаться по точной сумме, прежние суммы сохранят свои заказы.`)) return;
    lock.current = true; setBusy(true); setError('');
    try { await v4API.reuseCryptoAddress(row._id, { expectedRevision: row.revision, reason: 'Включение многоразового приёма через панель' });
      setReload(value => value + 1);
    } catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setBusy(false); }
  };
  const showHistory = async (row, targetPage = 1) => {
    const request = ++historyRequest.current;
    setHistory({ row, loading: true, items: [], page: targetPage, total: 0 });
    try {
      const pageData = await v4API.cryptoAddressPayments(row._id, { page: targetPage });
      if (request === historyRequest.current) setHistory({ row, ...pageData, loading: false });
    } catch (err) { if (request === historyRequest.current) setHistory({ row, items: [], page: targetPage, total: 0, error: errorMessage(err) }); }
  };
  const disabled = busy || !system?.ready;
  const count = addresses.split(/\r?\n/).filter(value => value.trim()).length;
  return <Stack spacing={2}>
    <Typography variant="h5">Криптоадреса BTC / LTC</Typography>
    <Alert severity="info">Постоянный пул BTC/LTC: одни и те же адреса используются для многих заказов одновременно.
      Заказ определяется по адресу и точной сумме. При совпадении суммы добавляются минимальные доли монеты.
      Подтверждение оплаты — минимум один блок. Отправлять нужно ровно указанную сумму, без округления, комиссию сети оплачивать сверху.
      Приватные ключи и seed не нужны. После оплаты средства остаются в вашем кошельке.</Alert>
    <Alert severity="warning">Адреса должны быть выделены только для V4 и не использоваться в legacy.
      Адреса с историей переводов допускаются. Выданные суммы не назначаются другому заказу даже после отмены или истечения срока.
      Небольшого пула, например пяти адресов на монету, достаточно для повторного приёма.</Alert>
    {error && <Alert severity="error">{error}</Alert>}{notice && <Alert severity="success">{notice}</Alert>}
    {!system?.ready && <Alert severity="warning">Сначала подготовьте V4 в разделе «Система».</Alert>}
    {pending && <Alert severity="warning">{pending.unreadable ? 'Сохранённый запрос не удалось прочитать. Проверьте список адресов и сбросьте форму.' :
      `Есть сохранённый импорт ${pending.body.addresses.length} адресов. Повторите тот же запрос, чтобы получить результат без дублирования.`}</Alert>}
    {pending && <Button disabled={busy} onClick={() => { sessionStorage.removeItem(storageKey); setPending(null); setConfirmed(false); setError(''); }}>
      Сбросить форму импорта (добавленные адреса сохранятся)
    </Button>}
    <Paper sx={{ p: 2 }}><Stack spacing={2}>
      <Typography variant="h6">Добавить адреса</Typography>
      <TextField select label="Процессинг BTC / LTC" value={processingId} disabled={disabled || !!pending} onChange={e => { setProcessingId(e.target.value); setConfirmed(false); }}>
        {processings.map(row => <MenuItem key={row._id} value={row._id}>{row.name} · {row.provider.toUpperCase()}{row.enabled ? '' : ' (отключён)'}</MenuItem>)}
      </TextField>
      {!processings.length && <Typography>Создайте процессинг с провайдером btc или ltc в разделе «Процессинги».</Typography>}
      <TextField label="Адреса — по одному на строку" multiline minRows={5} value={addresses} disabled={disabled || !!pending}
        onChange={e => { setAddresses(e.target.value); setConfirmed(false); }} helperText={`${count} / 100. Проверяется сеть, контрольная сумма и доступность блокчейна.`} />
      <TextField label="Причина импорта" value={reason} disabled={disabled || !!pending} onChange={e => { setReason(e.target.value); setConfirmed(false); }} />
      <FormControlLabel label="Адреса принадлежат мне, выделены только для V4 и не используются в legacy или других системах"
        control={<Checkbox checked={confirmed} disabled={disabled} onChange={e => setConfirmed(e.target.checked)} />} />
      <Button variant="contained" onClick={save} disabled={disabled || !confirmed || pending?.unreadable || (!pending && (!processingId || !reason.trim() || count < 1 || count > 100))}>
        {busy ? 'Проверка и сохранение…' : pending ? 'Повторить сохранённый импорт' : 'Добавить адреса'}
      </Button>
    </Stack></Paper>
    <Stack direction="row" spacing={2}>
      <TextField select label="Фильтр процессинга" value={filter} sx={{ minWidth: 240 }} onChange={e => { setFilter(e.target.value); setPage(1); }}>
        <MenuItem value="">Все</MenuItem>{processings.map(row => <MenuItem key={row._id} value={row._id}>{row.name}</MenuItem>)}
      </TextField><Button disabled={busy || loading} onClick={() => setReload(value => value + 1)}>Обновить</Button>
    </Stack>
    <Typography>Активных адресов: {data.counts.ready || 0} · Старых одноразовых занято: {data.counts.allocated || 0} · Выключено: {data.counts.retired || 0}</Typography>
    <TableContainer component={Paper}><Table size="small" aria-label="Пул криптоадресов"><TableHead><TableRow>
      {['Процессинг', 'Адрес / сеть', 'Состояние', 'Заказ', 'Сумма', 'Транзакция', 'Действие'].map(label => <TableCell key={label}>{label}</TableCell>)}
    </TableRow></TableHead><TableBody>{data.items.map(row => <TableRow key={row._id}>
      <TableCell>{row.processingName || row.processingId}</TableCell><TableCell sx={{ overflowWrap: 'anywhere' }}>{row.address}<br />{row.network}</TableCell>
      <TableCell>{row.chainPolicy === 'amount-v2' ? row.state === 'ready' ? 'Многоразовый · включён' : 'Многоразовый · выключен' :
        ({ ready: 'Одноразовый · свободен', allocated: 'Одноразовый · закреплён', retired: 'Выведен' })[row.state]}<br />Заказов: {row.totalPayments || 0}</TableCell>
      <TableCell>{row.payment?.orderId && <Link to={`/v4/orders/${encodeURIComponent(row.payment.orderId)}`}>{row.payment.orderId}</Link>}<br />{row.payment?.status}{row.payment?.reviewRequired && ' · Требует проверки'}</TableCell>
      <TableCell>{row.payment && <>К оплате: {formatMoney(row.payment.amountUnits, 8, row.payment.currency)}
        {row.payment.receivedUnits && <><br />Получено: {formatMoney(row.payment.receivedUnits, 8, row.payment.currency)}</>}</>}</TableCell>
      <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.payment?.transactionId || '—'}{typeof row.payment?.confirmations === 'number' &&
        <><br />{row.payment.chainConfirmed ? 'Подтверждений при зачёте' : 'Подтверждений'}: {row.payment.confirmations}</>}
        {row.payment?.transactionState === 'pending' && <><br />Мемпул — не оплата</>}
        {row.payment?.transactionState === 'not_seen' && <><br />Сейчас не видна в мемпуле</>}
      </TableCell>
      <TableCell><Stack>
        <Button disabled={disabled} onClick={() => showHistory(row)}>Все заказы</Button>
        {row.chainPolicy !== 'amount-v2' && (row.state === 'ready' || row.payment?.chainConfirmed) &&
          <Button disabled={disabled} onClick={() => reuse(row)}>Сделать многоразовым</Button>}
        {row.chainPolicy === 'amount-v2' && row.state === 'retired' && <Button disabled={disabled} onClick={() => reuse(row)}>Включить</Button>}
        {row.state === 'ready' && <Button color="warning" disabled={disabled} onClick={() => retire(row)}>Убрать из выдачи</Button>}
      </Stack></TableCell>
    </TableRow>)}</TableBody></Table></TableContainer>
    <TablePagination component="div" count={data.total} page={page - 1} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_, p) => setPage(p + 1)} />
    <Dialog open={!!history} onClose={() => { historyRequest.current++; setHistory(null); }} fullWidth maxWidth="lg">
      <DialogTitle>Заказы адреса {history?.row?.address}</DialogTitle>
      <DialogContent>
        {history?.error && <Alert severity="error">{history.error}</Alert>}
        {history?.loading ? <Typography>Загрузка…</Typography> : <Table size="small"><TableHead><TableRow>
          {['Заказ', 'Статус', 'Точная сумма', 'Добавка', 'Транзакция'].map(value => <TableCell key={value}>{value}</TableCell>)}
        </TableRow></TableHead><TableBody>{history?.items?.map(payment => <TableRow key={payment.leaseId}>
          <TableCell>{payment.orderId && <Link to={`/v4/orders/${encodeURIComponent(payment.orderId)}`}>{payment.orderId}</Link>}</TableCell>
          <TableCell>{payment.status}{payment.reviewRequired && ' · Требует проверки'}</TableCell>
          <TableCell>{formatMoney(payment.amountUnits, 8, payment.currency)}</TableCell>
          <TableCell>{formatMoney(payment.adjustmentUnits || '0', 8, payment.currency)}</TableCell>
          <TableCell sx={{ overflowWrap: 'anywhere' }}>{payment.transactionId || '—'}
            {payment.transactionState === 'pending' && <><br />Мемпул · 0 подтверждений</>}
            {payment.transactionState === 'not_seen' && <><br />Сейчас не видна в мемпуле</>}
          </TableCell>
        </TableRow>)}</TableBody></Table>}
        <TablePagination component="div" count={history?.total || 0} page={(history?.page || 1) - 1} rowsPerPage={25}
          rowsPerPageOptions={[25]} onPageChange={(_, p) => showHistory(history.row, p + 1)} />
      </DialogContent>
      <DialogActions><Button onClick={() => { historyRequest.current++; setHistory(null); }}>Закрыть</Button></DialogActions>
    </Dialog>
  </Stack>;
}
